import { Injectable } from '@angular/core';
import { CacheCollection } from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoCoordinate,
    MailingAddress,
} from '@polaris-customerweb/sharedcomponents.shared';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
    GeocoderResource,
    MapboxGeocodeResult,
} from './geocoder-resource.service';

@Injectable({ providedIn: 'root' })
export class GeocoderService {
    private geocodeCache = new CacheCollection<MailingAddress | undefined>();

    constructor(private resource: GeocoderResource) {}

    getLngLatByPlaceName(
        name: string,
        proximity?: number[]
    ): Observable<MapboxGeocodeResult> {
        return this.resource.getLngLatByPlaceName(name, proximity);
    }

    getPlaceNamesByLngLat(
        lng: number,
        lat: number
    ): Observable<MapboxGeocodeResult> {
        return this.resource.getPlaceNamesByLngLat(lng, lat);
    }

    getAddressByGeoCoordinate(
        geoCoordinate: GeoCoordinate,
        isStateAbbreviated = false
    ): Observable<MailingAddress | undefined> {
        const key = `${geoCoordinate.lng}${geoCoordinate.lat}`;
        this.geocodeCache.purgeOld(3600000);
        const cached = this.geocodeCache.getItem(key);
        if (cached) {
            return of(cached);
        }

        return this.resource
            .getAddressByLngLat(geoCoordinate?.lng, geoCoordinate?.lat)
            .pipe(
                map((result) => {
                    const feature = result?.features[0];
                    const contract: Record<string, string> = {};

                    if (!feature) {
                        return undefined;
                    }

                    if (feature.address) {
                        contract['street'] = `${feature.address} `;
                    }

                    if (feature.text) {
                        contract['street'] = `${contract['street'] || ''}${
                            feature.text
                        }`;
                    }

                    if (feature.context?.length > 0) {
                        contract['city'] =
                            feature.context.find((data) =>
                                data.id.includes('place')
                            )?.text || '';
                        const region = feature.context.find((data) =>
                            data.id.includes('region')
                        );
                        contract['state'] =
                            (isStateAbbreviated
                                ? region?.short_code?.split('-').pop()
                                : region?.text) || '';
                        contract['postalCode'] =
                            feature.context.find((data) =>
                                data.id.includes('postcode')
                            )?.text || '';
                        contract['country'] =
                            feature.context.find((data) =>
                                data.id.includes('country')
                            )?.text || '';
                    }

                    const address: MailingAddress = new MailingAddress(
                        contract
                    );

                    return address.isEmpty() ? undefined : address;
                }),
                tap((address) => {
                    this.geocodeCache.setItem(address, key);
                })
            );
    }

    /**
     * @param geoCoordinate
     * @param address
     * @description - Turn address or geoCoordinate into array of two strings.
     * @returns [ address1, address2 ] if address is valid, otherwise [ lat, lng ], each to 4 places.
     */
    getLocationString(
        geoCoordinate: GeoCoordinate,
        address?: MailingAddress
    ): string[] {
        if (address && !address.isEmpty()) {
            return [address.street, address.toShortString()];
        }

        return [
            `lat: ${geoCoordinate.lat.toFixed(4)}`,
            `lng: ${geoCoordinate.lng.toFixed(4)}`,
        ];
    }
}
